// FlightCompare.jsx — MakeBeeTrip (Redesigned)
// Compact card-grid layout. Clean, scannable, no giant table.

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import "./Compare.css";
import { Plane } from "lucide-react";
import { PricingModal } from "../Flights"; // ⚠️ adjust path to wherever Flights.jsx actually lives relative to Compare.jsx
import Login from "../../../Main/Login/Login";           // ⚠️ same relative-path rule as Flights.jsx uses
import Signup from "../../../Main/Signup/Signup";
import usePageMeta from "../../../../hooks/usePageMeta";

const COMPARE_META_KEY = "compareFlightsMeta";
const COMPARE_STORAGE_KEY = "compareFlights";
const fmt = (n) => `₹${Number(n).toLocaleString("en-IN")}`;

const AIRLINE_COLORS = {
  "6E": "#1a56db",
  SG: "#e84c3d",
  AI: "#c0392b",
  IX: "#7c3aed",
  UK: "#0e7490",
  G8: "#d97706",
  QP: "#059669",
};
const AIRLINE_NAMES = {
  "6E": "IndiGo",
  SG: "SpiceJet",
  AI: "Air India",
  IX: "Air India Express",
  UK: "Vistara",
  G8: "GoAir",
  QP: "Akasa Air",
};

function enrichFlight(f) {
  if (!f) return null;
  return {
    ...f,
    cabinBag: "7 Kg",
    checkInBag: f.stops === 0 ? "15 Kg" : "20 Kg",
    meals: (f.airlineLogo === "AI" || f.airlineLogo === "UK") ? "yes" : "paid",
    power: (f.airlineLogo === "UK" || f.airlineLogo === "AI") ? "yes" : "no",
    entertainment: (f.airlineLogo === "AI" || f.airlineLogo === "UK") ? "yes" : "no",
    wifi: f.airlineLogo === "UK" ? "yes" : "no",
    cancellation: f.refundable ? "Partial" : "Non-Refundable",
    dateChange: f.refundable ? "₹3,500" : "Not Allowed",
    taxes: fmt(Math.round((f.displayPrice || f.price) * 0.18 + 450)),
    flightNo: f.flightNumbers || "N/A",
    displayPrice: f.displayPrice || f.price,
  };
}

const SECTIONS = [
  {
    id: "flight",
    label: "Flight Info",
    emoji: <Plane size={14} />,
    fields: [
      { key: "flightNo", label: "Flight No." },
      { key: "duration", label: "Duration" },
      { key: "stops", label: "Stops", type: "stops" },
      { key: "departTime", label: "Departs" },
      { key: "arriveTime", label: "Arrives" },
    ],
  },
  {
    id: "baggage",
    label: "Baggage",
    emoji: "🧳",
    fields: [
      { key: "cabinBag", label: "Cabin Bag" },
      { key: "checkInBag", label: "Check-In" },
    ],
  },
  {
    id: "onboard",
    label: "On Board",
    emoji: "🍱",
    fields: [
      { key: "meals", label: "Meals", type: "tristate" },
      { key: "power", label: "Power Outlet", type: "bool" },
      { key: "entertainment", label: "Entertainment", type: "bool" },
      { key: "wifi", label: "Wi-Fi", type: "bool" },
    ],
  },
  {
    id: "policy",
    label: "Policy",
    emoji: "📋",
    fields: [
      { key: "cancellation", label: "Cancellation" },
      { key: "dateChange", label: "Date Change" },
    ],
  },
  {
    id: "price",
    label: "Price",
    emoji: "💰",
    fields: [
      { key: "displayPrice", label: "Base Fare", type: "price" },
      { key: "taxes", label: "Taxes & Fees" },
    ],
  },
];

function Cell({ fkey, value, type, isBest }) {
  if (type === "bool") {
    const yes = value === "yes";
    return (
      <div className={`cmp-cell ${isBest ? "cmp-cell--best" : ""}`}>
        <span className={`cmp-icon ${yes ? "cmp-icon--yes" : "cmp-icon--no"}`}>{yes ? "✓" : "✗"}</span>
        <span className={yes ? "cmp-good" : "cmp-bad"}>{yes ? "Available" : "Not Available"}</span>
      </div>
    );
  }
  if (type === "tristate") {
    const isYes = value === "yes";
    const isPaid = value === "paid";
    return (
      <div className={`cmp-cell ${isBest ? "cmp-cell--best" : ""}`}>
        <span className={`cmp-icon ${isYes ? "cmp-icon--yes" : isPaid ? "cmp-icon--paid" : "cmp-icon--no"}`}>
          {isYes ? "✓" : isPaid ? "₹" : "✗"}
        </span>
        <span className={isYes ? "cmp-good" : isPaid ? "cmp-paid" : "cmp-bad"}>
          {isYes ? "Free" : isPaid ? "Chargeable" : "No"}
        </span>
      </div>
    );
  }
  if (type === "stops") {
    const n = Number(value);
    const label = n === 0 ? "Non-Stop" : n === 1 ? "1 Stop" : `${n} Stops`;
    const cls = n === 0 ? "green" : n === 1 ? "amber" : "red";
    return (
      <div className="cmp-cell">
        <span className={`cmp-stop cmp-stop--${cls}`} />
        <span>{label}</span>
      </div>
    );
  }
  if (type === "price") {
    return (
      <div className={`cmp-cell cmp-cell--price ${isBest ? "cmp-cell--cheapest" : ""}`}>
        <span className="cmp-price-num">{fmt(value)}</span>
        {isBest && <span className="cmp-cheapest-tag">Cheapest ✓</span>}
      </div>
    );
  }
  return (
    <div className={`cmp-cell ${isBest ? "cmp-cell--best" : ""}`}>
      <span>{value || "—"}</span>
    </div>
  );
}

export default function FlightCompare({ flights = [], onClose, onRemove, pageMode = false, meta }) {
  const [highlight, setHighlight] = useState(true);
  const [bookingFlight, setBookingFlight] = useState(null);
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [showSignupModal, setShowSignupModal] = useState(false);

  const passengerMeta = meta || { adults: 1, children: 0, infants: 0, classType: "Economy/Premium Economy" };
  const enriched = flights.map(enrichFlight).filter(Boolean);
  if (enriched.length === 0) return null;

  const prices = enriched.map((f) => f.displayPrice);
  const cheapestIdx = prices.indexOf(Math.min(...prices));

  const getBestIdx = (field, type) => {
    if (!highlight) return -1;
    if (field === "displayPrice") return cheapestIdx;
    if (type === "bool" || type === "tristate")
      return enriched.findIndex((f) => f[field] === "yes");
    if (field === "checkInBag") {
      const w = enriched.map((f) => parseInt(f[field]));
      return w.indexOf(Math.max(...w));
    }
    if (field === "duration") {
      const mins = enriched.map((f) => {
        const parts = f.duration.replace(/[hm]/g, " ").trim().split(/\s+/);
        return Number(parts[0]) * 60 + (Number(parts[1]) || 0);
      });
      return mins.indexOf(Math.min(...mins));
    }
    if (field === "stops") {
      const s = enriched.map((f) => f.stops);
      return s.indexOf(Math.min(...s));
    }
    return -1;
  };

  const colCount = enriched.length;

  const panel = (
    <div className="cmp-panel" style={{ "--cols": colCount }}>

      {/* Header */}
      <div className="cmp-header">
        <div className="cmp-header-left">
          <div className="cmp-header-badge">⚖</div>
          <div>
            <div className="cmp-header-title">Compare Flights</div>
            <div className="cmp-header-sub">{colCount} of 3 flights selected</div>
          </div>
        </div>
        <div className="cmp-header-right">
          <label className="cmp-toggle">
            <input type="checkbox" checked={highlight} onChange={(e) => setHighlight(e.target.checked)} />
            <span className="cmp-toggle-track" />
            <span className="cmp-toggle-label">Best value</span>
          </label>
          {!pageMode && (
            <button className="cmp-x-btn" onClick={onClose}>✕ Close</button>
          )}
        </div>
      </div>

      {/* Flight hero columns */}
      <div className="cmp-grid cmp-grid--hero">
        <div className="cmp-label-col" />
        {enriched.map((f, i) => {
         const color = AIRLINE_COLORS[f.airlineLogo] || "#10b981";
          const name = AIRLINE_NAMES[f.airlineLogo] || f.airline;
          const isCheap = i === cheapestIdx;
          return (
            <div key={f.id} className={`cmp-hero ${isCheap ? "cmp-hero--cheap" : ""}`} style={{ "--c": color }}>
              {isCheap && <div className="cmp-best-ribbon">BEST PRICE</div>}
              <button className="cmp-remove" onClick={() => onRemove?.(f.id)}>✕</button>
              <div className="cmp-logo" style={{ background: color }}>{f.airlineLogo}</div>
              <div className="cmp-airline">{name}</div>
              <div className="cmp-route-times">
                <span className="cmp-t">{f.departTime}</span>
                <span className="cmp-plane-line"><Plane size={18} /></span>
                <span className="cmp-t">{f.arriveTime}{f.arriveNextDay ? <sup>+1</sup> : ""}</span>
              </div>
              <div className="cmp-airports">{f.departAirport} → {f.arriveAirport}</div>
              <div className="cmp-price-big" style={{ color }}>{fmt(f.displayPrice)}</div>
              <div className="cmp-per">per adult · incl. taxes</div>
              <button
                className="cmp-book-btn"
                style={{ background: color }}
                onClick={() => setBookingFlight(f)}
              >
                Book Now
              </button>
            </div>
          );
        })}
      </div>

      {/* Sections */}
      <div className="cmp-body">
        {SECTIONS.map((sec) => (
          <div key={sec.id} className="cmp-section">
            <div className="cmp-section-head">
              <span>{sec.emoji}</span> {sec.label}
            </div>
            {sec.fields.map((field) => {
              const bestIdx = getBestIdx(field.key, field.type);
              return (
                <div key={field.key} className="cmp-grid cmp-grid--row">
                  <div className="cmp-label-col">{field.label}</div>
                  {enriched.map((f, i) => (
                    <Cell
                      key={`${f.id}-${field.key}`}
                      fkey={field.key}
                      value={f[field.key]}
                      type={field.type}
                      isBest={highlight && i === bestIdx}
                    />
                  ))}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="cmp-footer">
        <span className="cmp-note">💡 Prices per adult · policies subject to fare type</span>
        <button className="cmp-clear-btn" onClick={() => onRemove?.("all")}>Clear All</button>
      </div>
    </div>
  );


  return (
    <>
      {pageMode ? (
        <div className="cmp-page-wrap">{panel}</div>
      ) : (
        <div className="cmp-overlay" onClick={onClose}>
          <div className="cmp-sheet" onClick={(e) => e.stopPropagation()}>{panel}</div>
        </div>
      )}

      {bookingFlight && (
        <PricingModal
          flight={bookingFlight}
          travelDate={bookingFlight.travelDate}
          onClose={() => setBookingFlight(null)}
          onLoginRequest={(mode = "login") => {
            setBookingFlight(null);
            mode === "signup" ? setShowSignupModal(true) : setShowLoginModal(true);
          }}
          activeFare="regular"
          isInternational={false}
          adults={passengerMeta.adults}
          children={passengerMeta.children}
          infants={passengerMeta.infants}
          classType={passengerMeta.classType}
        />
      )}

      {showLoginModal && (
        <Login onClose={() => setShowLoginModal(false)} onSwitchToSignup={() => { setShowLoginModal(false); setShowSignupModal(true); }} />
      )}
      {showSignupModal && (
        <Signup onClose={() => setShowSignupModal(false)} onSwitchToLogin={() => { setShowSignupModal(false); setShowLoginModal(true); }} />
      )}
    </>
  );
}

export function ComparePage() {
  usePageMeta(
  "Compare Flight Fares & Airlines – MakeBeeTrip",
  "Compare flight fares, timings, baggage allowance and cabin classes side by side. Choose the best flight for your trip with MakeBeeTrip's fare comparison.",
  "compare flights, flight fare comparison, airline comparison, flight price comparison, best flight deals, compare air tickets"
);
  const navigate = useNavigate();
  const [flights, setFlights] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    try {
      const raw = window.localStorage.getItem(COMPARE_STORAGE_KEY);
      setFlights(raw ? JSON.parse(raw) : []);
      const rawMeta = window.localStorage.getItem(COMPARE_META_KEY);
      if (rawMeta) setMeta(JSON.parse(rawMeta));
    } catch { setFlights([]); }
  }, []);

  const handleRemove = (id) => {
    const next = id === "all" ? [] : flights.filter((f) => f.id !== id);
    setFlights(next);
    try { window.localStorage.setItem(COMPARE_STORAGE_KEY, JSON.stringify(next)); } catch { }
    if (next.length === 0) navigate("/flights");
  };

  if (flights.length === 0) return (
    <div className="cmp-empty-wrap">
      <div className="cmp-empty-card">
        <span style={{ fontSize: 48 }}><Plane size={18} />️</span>
        <h2>No flights to compare</h2>
        <p>Go to flight search and add up to 3 flights.</p>
        <button className="cmp-book-btn" style={{ background: "#1d4ed8", width: "auto", padding: "10px 28px" }}
          onClick={() => navigate("/flights")}>
          Search Flights
        </button>
      </div>
    </div>
  );

  return (
    <div className="cmp-page-wrap">
      <div className="cmp-page-topbar">
        <button className="cmp-back-btn" onClick={() => navigate("/flights")}>← Back to results</button>
        <span className="cmp-page-heading">Flight Comparison</span>
      </div>
      <FlightCompare flights={flights} onClose={() => navigate("/flights")} onRemove={handleRemove} pageMode meta={meta} />
    </div>
  );
}

export function CompareTray({ selectedFlights, onOpen, onRemove }) {
  if (!selectedFlights?.length) return null;
  return (
    <div className="cmp-tray">
      <div className="cmp-tray-inner">
        <span className="cmp-tray-label">⚖ {selectedFlights.length}/3</span>
        <div className="cmp-tray-chips">
          {selectedFlights.map((f) => {
          const c = AIRLINE_COLORS[f.airlineLogo] || "#10b981";
            return (
              <div key={f.id} className="cmp-chip">
                <span className="cmp-chip-logo" style={{ background: c }}>{f.airlineLogo}</span>
                <div className="cmp-chip-info">
                  <span className="cmp-chip-name">{AIRLINE_NAMES[f.airlineLogo] || f.airline}</span>
                  <span className="cmp-chip-time">{f.departTime} → {f.arriveTime}</span>
                </div>
                <span className="cmp-chip-price">{fmt(f.displayPrice || f.price)}</span>
                <button className="cmp-chip-rm" onClick={() => onRemove(f.id)}>✕</button>
              </div>
            );
          })}
        </div>
        <button
          className="cmp-tray-btn"
          disabled={selectedFlights.length < 2}
          onClick={onOpen}
        >Compare Now →</button>
      </div>
    </div>
  );
}